import { QUALITY_TIERS, QualityTier, defaultTier } from "./quality";

/**
 * Per-setting graphics overrides on top of the quality preset, chosen in Settings → Graphics and kept in
 * localStorage. `null` means "follow the preset". The preset itself is the quality tier (quality.ts), saved by the
 * governor's own store; picking a preset clears these overrides so it means what it says.
 *
 * Only settings that remove real per-frame work are offered: render resolution (fill), shadows (a second scene pass
 * and a filtered lookup per lit pixel), bloom (a mip chain), ambient occlusion (a full-scene normal pass plus samples),
 * MSAA (fragment work per sample) and the ground scatter (instances). Presentation-only; nothing here touches the sim.
 */
export interface GraphicsPrefsData {
  resolution: number | null;
  shadows: "off" | "hard" | "soft" | null;
  bloom: boolean | null;
  ao: boolean | null;
  msaa: boolean | null;
  grass: number | null;
}

export const RESOLUTIONS: readonly number[] = [0.75, 1, 1.5, 2];
export const GRASS_LEVELS: readonly { label: string; value: number }[] = [
  { label: "Off", value: 0 }, { label: "Some", value: 0.5 }, { label: "Full", value: 1 },
];
/** MSAA samples when the override turns it on (4× resolves on tile on Apple GPUs; more buys little). */
export const MsaaSamples = 4;
/**
 * Bloom render scale when bloom is switched on (the override here, the perf overlay's switch: SceneRig.setBloom) in a
 * tier that has it off: half-resolution bloom, as on `medium`.
 */
export const BloomOnScale = 0.5;

const KEY = "highmarch.graphics";
const EMPTY: GraphicsPrefsData = { resolution: null, shadows: null, bloom: null, ao: null, msaa: null, grass: null };

function load(): GraphicsPrefsData {
  try { return { ...EMPTY, ...(JSON.parse(localStorage.getItem(KEY) ?? "{}") as Partial<GraphicsPrefsData>) }; } catch { return { ...EMPTY }; }
}

/**
 * The preset hooks the scene registers, so the Graphics sheet can read and choose the tier. `followsDefault` is
 * true while the tier is the device's recommended one; `useDefault` returns to it; `defaultIndex` is that tier.
 */
interface PresetPort { index(): number; set(i: number): void; followsDefault(): boolean; useDefault(): void; defaultIndex(): number }

const listeners: (() => void)[] = [];
let preset: PresetPort | null = null;

export const GraphicsPrefs = {
  current: load(),
  set(patch: Partial<GraphicsPrefsData>): void {
    GraphicsPrefs.current = { ...GraphicsPrefs.current, ...patch };
    try { localStorage.setItem(KEY, JSON.stringify(GraphicsPrefs.current)); } catch { /* private mode */ }
    for (const l of listeners) l();
  },
  reset(): void { GraphicsPrefs.set({ ...EMPTY }); },
  get customised(): boolean { return Object.values(GraphicsPrefs.current).some((v) => v !== null); },
  /** Calls `l` after every change; returns the unsubscribe. */
  onChange(l: () => void): () => void {
    listeners.push(l);
    return () => { const i = listeners.indexOf(l); if (i >= 0) listeners.splice(i, 1); };
  },
  bindPreset(p: PresetPort): void { preset = p; },
  /** The preset in use; before the scene binds one, the tier a device starts on with nothing saved. */
  presetIndex(): number { return preset?.index() ?? defaultTier(); },
  /** The device's recommended preset (quality.ts defaultTier). */
  recommendedIndex(): number { return preset?.defaultIndex() ?? defaultTier(); },
  /** On Recommended: the recommended tier and no overrides. This is where every device starts. */
  get recommended(): boolean { return (preset?.followsDefault() ?? true) && !GraphicsPrefs.customised; },
  /** The recommended tier with the overrides cleared: the device default, which later launches follow too. */
  chooseRecommended(): void {
    GraphicsPrefs.current = { ...EMPTY };
    try { localStorage.setItem(KEY, JSON.stringify(GraphicsPrefs.current)); } catch { /* private mode */ }
    preset?.useDefault();
    for (const l of listeners) l();
  },
  /** Picks a preset and clears the overrides, so the preset is exactly what the player sees. */
  choosePreset(i: number): void {
    GraphicsPrefs.current = { ...EMPTY };
    try { localStorage.setItem(KEY, JSON.stringify(GraphicsPrefs.current)); } catch { /* private mode */ }
    preset?.set(i);
    for (const l of listeners) l();
  },
};

/** The tier the renderer actually uses: the preset with the player's overrides applied. */
export function effectiveTier(t: QualityTier, p: GraphicsPrefsData = GraphicsPrefs.current): QualityTier {
  const shadows = p.shadows === null ? t.shadows : p.shadows !== "off";
  const softShadows = p.shadows === null ? t.softShadows : p.shadows === "soft";
  return {
    ...t,
    pixelRatio: p.resolution ?? t.pixelRatio,
    shadows,
    softShadows,
    bloom: p.bloom === null ? t.bloom : p.bloom ? Math.max(t.bloom, BloomOnScale) : 0,
    ao: p.ao ?? t.ao,
    msaa: p.msaa === null ? t.msaa : p.msaa ? MsaaSamples : 0,
  };
}

/**
 * The effective tier the main renderer last applied (SceneRig.applyTier), for secondary canvases that should cost no
 * more than the game itself (the reward chest's stage); before the first apply, the device default with the overrides.
 */
export function appliedTier(): QualityTier { return applied ?? effectiveTier(QUALITY_TIERS[defaultTier()]); }
export function setAppliedTier(t: QualityTier): void { applied = t; }
let applied: QualityTier | null = null;

/** Ground-scatter density for the next level built: the override, else full. */
export function grassDensity(): number { return GraphicsPrefs.current.grass ?? 1; }
